import logger from 'OK/logger';
import { prepareForPost, ajax, trigger, updateBlocks } from 'OK/utils/vanilla';
import { traverseParents, firstByClass } from 'OK/utils/dom';
import Delegate from 'OK/utils/delegate';
import AbstractShortcutMenu from 'OK/AbstractShortcutMenu';
import a11yConfiguration from 'OK/pms!accessibilityConfiguration';

var areAccessibleDropDownMenusEnabled =
    Boolean(a11yConfiguration && a11yConfiguration.areAccessibleDropDownMenusEnabled);

export default class ShortcutMenu extends AbstractShortcutMenu {
    constructor(hookId) {
        super();

        this.hookId = hookId;
        this.stopClick = true;
        this.ajaxRequest = null;
        this.cacheable = true;
        this.loaded = false;
        this.showOnClick = true;
        this.showOnHover = false;
    }

    /**
     * Activate hook.
     * Add all event listeners to element that previous from hook element
     * @param {HTMLElement} element hook element
     */
    activate(element) {
        // Количество миллисекунд, в течении которых надо игнорировать наведение мыши на элемент,
        // с которым связано меню (см. `AbstractShortcutMenu.prototype.hoverInListener`)
        var ignoreHover = parseInt(element.getAttribute('data-ignoreHoverTime'), 10);
        this.element = element;
        this.blockId = this.element.getAttribute('data-blockid');
        this.block = document.getElementById('hook_Block_' + this.blockId);
        this.inplace = this.element.getAttribute('data-inplace');
        this.loaded = this.element.getAttribute('data-loaded');
        this.showDelay = parseInt(this.element.getAttribute('data-show'), 10) || 300;
        this.hideDelay = parseInt(this.element.getAttribute('data-hide'), 10) || 100;
        this.cacheable = !this.element.getAttribute('data-nocache');
        this.saveChanges = this.element.getAttribute('data-save-changes');
        this.direction = this.element.getAttribute('data-direction');
        this.position = this.element.getAttribute('data-position') || 'center';
        this.showOnClick = this.element.getAttribute('data-onClick');
        this.showOnHover = this.element.getAttribute('data-onHover');
        this.showAfterItemFocusIn = element.getAttribute('data-show-after-item-focus-in') === '1';
        this.isShowImmediately = this.element.getAttribute('data-showImmediately');
        this.showImmediatelySelector = this.element.getAttribute('data-showImmediatelySelector');
        this.relatedMenu = this.getRelatedMenu();
        this.hideOnClick = this.element.getAttribute('data-hideOnClick');
        this.hideOnMouseDown = this.element.getAttribute('data-hideOnMouseDown');
        // Чтобы тело меню sc-menu не исчезало по клику, добавить класс js-doNotHide
        this.hideOnScroll = this.element.getAttribute('data-hideOnScroll');
        this.hideAfterItemFocusOut = this.element.getAttribute('data-hide-after-item-focus-out') === '1';
        this.hideOnBlurSelector = this.element.getAttribute('data-hideOnBlurSelector');
        this.hideOnBlurElement = null;
        this.menuName = this.element.getAttribute("data-menuName");
        this.postRequest = this.element.getAttribute("data-postRequest") === '1';
        /*
            Чтобы меню правильно позиционировалось в скроллируемом контейнере (не window).
            Пока работает с direction и position 'auto'.
          */
        this.detectScrollableContainer = this.element.getAttribute('data-detect-scrollable');
        this.proxiedEvents = element.getAttribute('data-proxied-events');

        if (!isNaN(ignoreHover)) {
            this.ignoreHoverTime = ignoreHover;
        }

        this.holder = this.getHolder();
        if (!this.holder) {
            // По какой-то причине нет элемента-триггера
            logger.error('asm', 'noholder');
            logger.success('asm', 'noholder', this.buildParamFingerprint());
            return;
        }
        this.withA11yAttr = this.holder.hasAttribute("aria-expanded");

        this.handlers = {};
        this.boundAjaxFail = this.ajaxFail.bind(this);
        this.boundAjaxDone = this.ajaxDone.bind(this);
        this.showMenu = this.showMenu.bind(this);
        this.toggleMenuOnKeyPress = this.toggleMenuOnKeyPress.bind(this);

        this.toggleAriaExpanded = this.toggleAriaExpanded.bind(this);

        this.holderDelegate = new Delegate(this.holder);

        if (OK.device.isTouch) {
            this.holderDelegate.on('touchstart', this.holderTouchStartListener);

            // Если холдер ШМ имеет какое-то отношение к переходу по ссылке, то
            // первый клик должен вызывать только лишь отрисовку ШМ, а второй вызывать переход.
            // Холдер может относиться разным образом: содержать ссылку внутри себя,
            // сам быть ссылкой или быть её потомком
            var a = this.holder.getElementsByTagName('a')[0];
            if (!a) {
                a = traverseParents(this.holder, function (currentElement) {
                    return currentElement.tagName.toUpperCase() === 'A';
                }, document.body, true);
            }

            if (a) {
                // found gwt-bound link
                // bind to capturing event phase
                // a bit hacky, but long tap_in produces link click that cannot be prevented
                // except blocking all touch events
                this.linkDelegate = new Delegate(a);
                this.linkDelegate.on('touchstart', this.touchHandle.bind(this), true);
            }
        }

        this.holderDelegate.on('mouseleave', this.hoverOutListener.bind(this));

        if (this.showOnHover || !this.showOnClick) {
            this.holderDelegate.on('mouseenter', this.hoverInListener.bind(this));
        }

        if (this.showOnClick) {
            this.oneClickListener = this.addOneTimeListener(this.holder, 'click', this.showMenu);
            this.oneKeyPressListener = this.addOneTimeListener(this.holder, 'keypress', this.toggleMenuOnKeyPress);
        } else {
            if (this.hideOnClick) {
                this.holderDelegate.on('click', this.hoverOutListener.bind(this));
            }
            if (this.hideOnMouseDown) {
                this.holderDelegate.on('mousedown', this.hoverOutListener.bind(this));
            }
        }

        // Навесить обработчики сразу, если всё уже в дереве, потому что потом не сможем
        if (this.inplace && this.loaded) {
            this.initListeners(this.getBlock());
        }

        if (this.isShowImmediately) {
            this.showImmediately();
        }

        if (element.getAttribute('data-expanded') === '1') {
            // в этот момент ещё идёт активация хуков, потому из-за
            // манипуляций с хуками при открытии ШМ сразу его показывать нельзя
            setTimeout(this.showMenu, 0);
        }
        this.activateTime = Date.now();
    }

    /**
     * Deactivate hook.
     */
    deactivate() {
        if (!this.holder) {
            // По какой-то причине нет элемента-триггера
            return;
        }
        this.ajaxClose();
        this.hide();

        this.removeOneTimeListener(this.oneClickListener);
        this.removeOneTimeListener(this.oneKeyPressListener);
        this.removeOneTimeListener(this.oneScrollListener);
        this.removeOneTimeListener(this.oneBodyTouchListener);
        this.removeOneTimeListener(this.oneImmediatelyHandler);
        this.removeOneTimeListener(this.oneBlurListener);
        this.holderDelegate.destroy();
        if (this.linkDelegate) {
            this.linkDelegate.destroy();
        }
        if (this.shortcutMenuDelegate) {
            this.shortcutMenuDelegate.destroy();
        }
        this.removeFocusItemListeners();
    }

    buildParamFingerprint() {
        var params = [this.inplace, this.loaded, this.cacheable, this.showOnClick, this.hideOnClick, this.hideOnScroll];
        var result = '';

        params.forEach(function (value) {
            result += (!!value * 1).toString();
        });

        return result;
    }

    getHolder() {
        var holderId = this.element.getAttribute('data-holder');
        return holderId
            ? document.getElementById(holderId)
            : this.element.previousElementSibling;
    }

    getRelatedMenu() {
        var relatedMenuId = this.element.getAttribute('data-related'), hook;

        if (relatedMenuId) {
            hook = OK.hookModel.getHookById(relatedMenuId);
            return hook && hook.getInstance ? hook.getInstance() : null;
        }
        return null;
    }

    /**
     * @override
     * @returns {string}
     */
    getHtml() {
        if (!this.block) {
            return ""; //никого нет дома. добавим блок, если нужно будет реально что-то записать.
        }
        var data = this.block.innerHTML;
        if (!this.inplace || !this.cacheable) {
            //Деактивируем все дочерние хуки в теле inplace block но оставляем в нем html
            OK.hookModel.setHookContent(this.blockId, '');
            if (this.cacheable) {
                this.block.innerHTML = data;
            }
        }
        return data;
    }

    initBlock() {
        if (this.element && !this.block) {
            var childrenCollection = Array.prototype.slice.call(this.element.children);
            var find = null;
            for (var collectionIndex = 0; collectionIndex < childrenCollection.length; collectionIndex++) {
                if (childrenCollection[collectionIndex].classList.contains('posR')) {
                    find = childrenCollection[collectionIndex];
                    break;
                }
            }

            if (!find) {
                //вставляем hookBody, если его не было. сразу не надо, что зря не грузить лишний html пользователю.
                var blockElem = document.createElement('div');
                blockElem.id = 'hook_Block_' + this.blockId;
                blockElem.className = 'posR';
                this.element.appendChild(blockElem);

                this.block = blockElem;

                OK.hookModel.captureBlockHook(this.hookId, blockElem);
            } else {
                this.block = find;
            }
        }
    }

    focusInItemHandler(event) {
        if (this.element.contains(event.target)) {
            this.showMenu();
        }
    }

    focusOutItemHandler(event) {
        // relatedTarget - это следующий элемент, на который перешёл фокус. Если этот элемент не принадлежит меню, то скрываем окно
        if (!this.element.contains(event.relatedTarget)) {
            this.hideMenu();
        }
    }

    toggleFocusItemListeners(props) {
        var focusInItemHandler = this.focusInItemHandler.bind(this);
        var focusOutItemHandler = this.focusOutItemHandler.bind(this);

        if (this.showAfterItemFocusIn && this.hideTimeout === null) {
            if (props.add) {
                this.element.addEventListener('focusin', focusInItemHandler);
            } else {
                this.element.removeEventListener('focusin', focusInItemHandler);
            }
        }
        if (this.hideAfterItemFocusOut && this.showTimeout === null) {
            if (props.add) {
                this.element.addEventListener('focusout', focusOutItemHandler);
            } else {
                this.element.removeEventListener('focusout', focusOutItemHandler);
            }
        }
    }

    addFocusItemListeners() {
        this.toggleFocusItemListeners({add: true});
    }

    removeFocusItemListeners() {
        this.toggleFocusItemListeners({add: false});
    }

    /**
     * Инициализация обработчиков внутри ШМ
     * @param {HTMLElement} listenerBlock
     */
    initListeners(listenerBlock) {
        AbstractShortcutMenu.prototype.initListeners.call(this, listenerBlock);
        this.addFocusItemListeners();

        if (this.hideOnBlurSelector) {
            this.hideOnBlurElement = this.block.querySelector(this.hideOnBlurSelector);
            this.oneBlurListener = this.addOneTimeListener(this.hideOnBlurElement, 'blur', this.hoverOutListener.bind(this));
        }
    }

    /**
     * Make ajax request to 'data-url' attribute and then show shortcut menu
     * @override
     */
    showMenu() {
        clearTimeout(this.hideTimeout);
        this.showTimeout = null;

        // Если связанное меню вызвано на отрисовку ховером по якорю, оно будет отрисовано с таймаутом.
        // Если текущее меню вызвано кликом, оно будет отрисовано мгновенно.
        // Таким образом, связанное меню может закрывать собой текущее, если клик и ховер будут почти одновременны
        // Эта ситуация происходит, например, при автотестах
        // При этом связанное меню не будет доступно в activeInstance, т.к. отрисовка еще не произошла
        if (this.relatedMenu && this.relatedMenu.hide) {
            this.relatedMenu.hide();
        }

        //Точно такое же меню от этого элемента уже показано. Не надо ничего делать
        if (this.visible()) {
            return;
        }

        if (AbstractShortcutMenu.getActiveInstance()) {
            // Не должно быть открыто больше 1 ШМ
            AbstractShortcutMenu.getActiveInstance().hide();
        }

        if (this.showOnClick) {
            this.removeOneTimeListener(this.oneClickListener);
            this.removeOneTimeListener(this.oneKeyPressListener);
            this.oneClickListener = this.addOneTimeListener(this.holder, 'click', this.hideMenu);
            this.oneKeyPressListener = this.addOneTimeListener(this.holder, 'keypress', this.toggleMenuOnKeyPress);
        }

        if (this.hideOnScroll) {
            this.oneScrollListener = this.addOneTimeListener(this.getClosestScrollable(true), 'scroll', this.hoverOutListener);
        }

        this.showTimeout = null;
        if (OK.device.isTouch) {
            this.oneBodyTouchListener = this.addOneTimeListener(document.body, 'touchstart', this.hoverOutListener);
        }

        if (this.ajaxRequest) {
            return;
        }

        this.startTime = Date.now();

        if (this.loaded) {
            var html = this.getHtml();
            this.processHTML(html, undefined, !this.inplace || !this.cacheable);

            var secondLogParam = this.menuName ? 'cache.' + this.menuName : 'cache';
            logger.success('asm', 'show', secondLogParam);
            this.toggleAriaExpanded(true);
            if (document.documentElement.hasAttribute('data-use-hook-activator')) {
                var menuElement = this.menuElement;
                import('OK/HookViewportActivator').then(m => m && m.default || m).then(function (HookViewportActivator) {
                    HookViewportActivator.checkArea(menuElement);
                });
            }
        } else {
            var url = this.element.getAttribute('data-url');

            if (this.postRequest) {
                const postData = {};
                const requestUrl = prepareForPost(url, postData);
                this.ajaxRequest = ajax({
                    url: requestUrl,
                    type: 'POST',
                    data: postData,
                    dataType: 'html'
                });
            } else {
                this.ajaxRequest = ajax({
                    url: url,
                    dataType: 'html'
                });
            }
            this.ajaxRequest.then(
                this.boundAjaxDone,
                this.boundAjaxFail
            );
        }
    }

    toggleMenuOnKeyPress(e) {
        e.preventDefault();
        if ((e.keyCode === ENTER_KEY_CODE) || (e.keyCode === SPACE_KEY_CODE)) {
            if (this.visible()) {
                this.hideMenu();
            } else {
                this.showMenu();
            }
        }
    }

    /**
     * Отображаем ШМ как только отрендерились.
     */
    showImmediately() {
        var self = this;
        if (self.showImmediatelySelector) {
            // Задержка, чтобы хуки в меню активировались
            setTimeout(function () {
                // Показываем ШМ, только если мышку далеко не увели.
                var closestSelectorName = self.showImmediatelySelector + ':hover';
                self.showImmediatelyElement = traverseParents(self.holder, function (element) {
                    return element.matches(closestSelectorName);
                });

                // Можем дойти до корня, это нехорошо
                if (self.showImmediatelyElement.matches(closestSelectorName)) {
                    self.showMenu();
                    // Скрываем ШМ, если сразу уводим мышку далеко.
                    // Если не сделать - меню может никогда не скрыться.
                    self.oneImmediatelyHandler = self.addOneTimeListener(self.showImmediatelyElement, 'mouseleave', function () {
                        setTimeout(function () {
                            if (!self.menuElementHoverInListenerCalled) {
                                self.hideMenu();
                            }
                        }, self.hideDelay);
                    });
                }
            }, 1);
        }
    }

    /**
     * Hide shortcut menu
     * @override
     */
    hideMenu() {

        // блокируем таймаут показа меню, т.к. уже показывать меню не надо
        this.clearShowTimeout();

        // Может быть видимым только 1 экземпляр ШМ.
        // Игнорируем кейс, когда скрыться пытается элемент, который не был отрисован (быстрый провод мышью по якорю)
        // Тут могут быть проблемы, если есть связанное меню — в этом случае требование видимости одного экземпляра может быть нарушено
        // Сейчас связанное меню может быть только по клику, для решения добавлена проверка по этому признаку
        if (!this.visible() && !this.showOnClick) {
            return;
        }

        this.clearHideTimeout();
        this.stopClick = true;

        if (this.showOnClick) {
            this.removeOneTimeListener(this.oneClickListener);
            this.removeOneTimeListener(this.oneKeyPressListener);
            this.oneClickListener = this.addOneTimeListener(this.holder, 'click', this.showMenu);
            this.oneKeyPressListener = this.addOneTimeListener(this.holder, 'keypress', this.toggleMenuOnKeyPress);
        }

        this.ajaxClose(function () {
            logger.success('asm', 'ajax', 'to');
        });
        this.toggleAriaExpanded(false);
        if (this.menuElement) {
            this.menuElement.classList.add('sc-menu__hidden');
            this.getHtml();
            if (this.saveChanges && !this.inplace && this.loaded && this.cacheable) {
                // Для случая, если в ШМ может меняться состояние (html),
                // сохраняем измененный html для следующего показа ШМ
                this.initBlock();
                this.block.innerHTML = this.getBlock().innerHTML;
            }
            OK.hookModel.setHookContent(AbstractShortcutMenu.getBlockId(), '');
            this.menuElement = null;
        }
        this.holder.classList.remove('__vis');
        trigger(this.holder, 'sm:hide');
        if (this.shortcutMenuDelegate && !this.cacheable) {
            // Отписка от событий внутри ШМ
            this.shortcutMenuDelegate.off();
        }

        this._visible = false;

        if (areAccessibleDropDownMenusEnabled) {
            this.holder.ariaExpanded = 'false';
        }

        AbstractShortcutMenu.setActiveInstance(null);
    }

    /**
     * Listener that will catch first touch on element and ignore it
     * Second click will close menu
     * @param {Event} e event
     * @return {?boolean}
     */
    touchHandle(e) {
        if (this.stopClick) {
            e.preventDefault();
            e.stopPropagation();
            this.stopClick = false;
            if (!this.showOnClick) {
                this.hoverInListener();
            }
            return false;
        } else {
            this.hoverOutListener();
        }
    }

    /**
     * Appends HTML to DOM and sets up position
     * @override
     * @param {string} html of menu (XHR result or saved copy)
     * @param {XMLHttpRequest} xhr
     * @param {boolean} isUpdateBlock
     */
    processHTML(html, xhr, isUpdateBlock) {
        var blockIds = [];
        if (xhr && xhr.getResponseHeader(OK.navigation.HEADER)) {
            blockIds = xhr.getResponseHeader(OK.navigation.HEADER).split(",");
        }

        if (this.inplace) {
            this.initBlock();
            blockIds[0] = this.blockId;
        } else {
            blockIds[0] = AbstractShortcutMenu.getBlockId();
        }

        var b = this.getBlock();

        if (isUpdateBlock) {
            updateBlocks(html, blockIds);
            // Будем вешать листенеры на содержимое меню только при его вставке в дерево
            this.initListeners(b);
        }

        this.menuElement = firstByClass(b, 'sc-menu');
        this.positingElement();

        this.menuElement.classList.remove('sc-menu__hidden');
        this.holder.classList.add('__vis');
        trigger(this.holder, 'sm:show');
        AbstractShortcutMenu.setActiveInstance(this);
        this._visible = true;

        if (areAccessibleDropDownMenusEnabled) {
            this.holder.ariaExpanded = 'true';
        }
    }

    /**
     * Function that will be called on ajax done
     * @param {!Object} result ajax response
     */
    ajaxDone(result) {
        logger.duration('asm', Date.now() - this.startTime, 'ajax');
        var data = result.response;

        // Видимо, нас интересуют только непустые ответные строки
        if (!data || (data.split(OK.navigation.SPLITER)[0]).length === 0) {
            logger.success('asm', 'ajax', 'e');
            return;
        }
        this.toggleAriaExpanded(true);
        const secondLogParam = this.menuName ? 'ajax.' + this.menuName : 'ajax';
        logger.success('asm', 'show', secondLogParam);

        if (this.cacheable && this.element) { // prevent saving html after module deactivation
            this.loaded = true;
            this.initBlock();
            this.block.innerHTML = data.split(OK.navigation.SPLITER)[0];
        }
        // Если в процессе загрузки содержимого меню было вызвано его скрытие
        // (например, по причине того, что показывается другое меню, для которого это меню является `relatedMenu`),
        // отменяем отображение.
        // Флаг `cancelShow` может быть установлен в `AbstractShortcutMenu.prototype.hide`.
        // При этом в случае включенного кэширования (флаг `cacheable`) содержимое меню будет сохранено
        // для последующего использования (см. код выше).
        if (!this.cancelShow) {
            this.processHTML(data, result.xhr, true);
        }

        this.ajaxClose();
    }

    /**
     * Function that will be called on ajax fail
     */
    ajaxFail() {
        this.ajaxClose(function () {
            logger.error('asm', 'ajax');
        });
    }

    addOneTimeListener(element, type, handler) {
        var that = this;
        var oneTimeHandler = function oneTimeHandler(e) {
            element.removeEventListener(e.type, oneTimeHandler);
            handler.call(that, e);
        };
        element.addEventListener(type, oneTimeHandler);

        return {
            element: element,
            type: type,
            handler: oneTimeHandler
        };
    }

    removeOneTimeListener(listenerObject) {
        if (listenerObject) {
            listenerObject.element.removeEventListener(listenerObject.type, listenerObject.handler);
            listenerObject = null;
        }
    }

    toggleAriaExpanded(flag) {
        if (this.withA11yAttr && this.holder) {
            this.holder.setAttribute("aria-expanded", flag);
        }
    }
}

var ENTER_KEY_CODE = 13,
    SPACE_KEY_CODE = 32;
